import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

interface WizardHeaderContextValue {
  description: ReactNode | null;
  setDescription: (next: ReactNode | null) => void;
}

const WizardHeaderContext = createContext<WizardHeaderContextValue | null>(
  null,
);

export const WizardHeaderProvider = ({
  children,
}: PropsWithChildren<unknown>): JSX.Element => {
  const [description, setDescription] = useState<ReactNode | null>(null);
  const location = useLocation();

  // Clear on route change so a step that forgets to call useStepHeader
  // renders empty rather than inheriting the previous page's text.
  useEffect(() => {
    setDescription(null);
  }, [location.pathname]);

  const value = useMemo(() => ({ description, setDescription }), [description]);

  return (
    <WizardHeaderContext.Provider value={value}>
      {children}
    </WizardHeaderContext.Provider>
  );
};

export const useWizardHeader = (): WizardHeaderContextValue => {
  const ctx = useContext(WizardHeaderContext);
  if (ctx == null) {
    throw new Error(
      'useWizardHeader must be used inside <WizardHeaderProvider>',
    );
  }
  return ctx;
};

interface UseStepHeaderArgs {
  description?: ReactNode;
}

// Step pages call this once at the top to publish their description into the
// shell's WizardHeaderCard. Re-runs whenever the passed value changes (e.g.
// dynamic descriptions that read runtime state), and is cleared automatically
// on route change by the provider.
export const useStepHeader = ({ description }: UseStepHeaderArgs): void => {
  const { setDescription } = useWizardHeader();
  useEffect(() => {
    setDescription(description ?? null);
  }, [description, setDescription]);
};
